import * as React from 'react'

import { Box, Button, CollapsableCard, Link, Text } from '@factorialco/deprecated-design-system'
import { F0AvatarPerson } from '@factorialco/f0-react'
import { type BenefitsEmployeeActivation } from 'generated/resources/graphql'

import Sidepanel from 'modules/core/components/Sidepanel'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { buildUrl } from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'

import { CalloutResults } from '../CalloutResults'

export interface EmployeeDetails {
  firstName: string
  lastName: string
  avatarUrl?: string
}

export type EmployeeDetailsMap = Record<number, EmployeeDetails>

const getErrorTranslation = (errorFieldName: string): string => {
  switch (errorFieldName) {
    case 'alreadyHasCard':
      return i18n.t('benefits.tabs.employees.activation_results.fields.already_has_card')
    case 'missingBirthday':
      return i18n.t('benefits.tabs.employees.activation_results.fields.missing_birthday')
    case 'missingIdentifier':
      return i18n.t('benefits.tabs.employees.activation_results.fields.missing_identifier')
    case 'missingPhone':
      return i18n.t('benefits.tabs.employees.activation_results.fields.missing_phone')
    case 'missingCountryCode':
      return i18n.t('benefits.tabs.employees.activation_results.fields.missing_country_code')
    case 'notWithinCountry':
      return i18n.t('benefits.tabs.employees.activation_results.fields.not_within_country')
    case 'notInPayroll':
      return i18n.t('benefits.tabs.employees.activation_results.fields.not_in_payroll')
    case 'otherErrors':
      return i18n.t('benefits.tabs.employees.activation_results.fields.other_errors')
    default:
      return errorFieldName
  }
}

interface Props {
  open: boolean
  onClose: () => void
  activationData?: BenefitsEmployeeActivation & {
    employeeDetailsMap?: EmployeeDetailsMap
  }
}

const ResultSection: React.FC<{
  title: string
  count: number
  employeeIds?: number[]
  employeeDetailsMap?: EmployeeDetailsMap
  navigation: ReturnType<typeof useNavigation>['navigation']
  showReviewLink?: boolean
}> = ({ title, count, employeeIds, employeeDetailsMap, navigation, showReviewLink = true }) => {
  const [expanded, setExpanded] = React.useState(false)

  if (count === 0) return null

  return (
    <Box flexDirection="column" gap="s8">
      <CollapsableCard
        Card={
          <Box
            flexDirection="row"
            justifyContent="spaceBetween"
            alignItems="center"
            onClick={() => setExpanded(!expanded)}
            cursor="pointer"
          >
            <Text size="300" weight="semibold" color="grey1000">
              {`${title}: ${count}`}
            </Text>
            <Button iconName={expanded ? 'chevron-up' : 'chevron-down'} type="secondary" />
          </Box>
        }
        Content={
          employeeIds && employeeIds.length > 0 ? (
            <Box paddingTop="s16" flexDirection="column" gap="s8">
              {employeeDetailsMap ? (
                employeeIds.map((employeeId) => {
                  const employee = employeeDetailsMap[employeeId]

                  if (!employee) {
                    return (
                      <Text.Normal key={employeeId} size="200" color="grey700">
                        Employee ID: {employeeId}
                      </Text.Normal>
                    )
                  }

                  return (
                    <Box
                      key={employeeId}
                      flexDirection="row"
                      alignItems="center"
                      justifyContent="spaceBetween"
                      gap="s8"
                    >
                      <Box flexDirection="row" alignItems="center" gap="s16">
                        <F0AvatarPerson
                          firstName={employee.firstName}
                          lastName={employee.lastName}
                          size="medium"
                          src={employee.avatarUrl ? buildUrl(employee.avatarUrl) : undefined}
                        />

                        <Text.Normal size="200" color="grey1000" weight="semibold">
                          {employee.firstName} {employee.lastName}
                        </Text.Normal>
                      </Box>

                      {showReviewLink && (
                        <Link.Href
                          external
                          target="_blank"
                          type="solid"
                          href={navigation.employees.id.fullPath({ id: employeeId })}
                        >
                          {i18n.t('benefits.tabs.employees.activation_results.review_data')}
                        </Link.Href>
                      )}
                    </Box>
                  )
                })
              ) : (
                <Text.Normal size="200" color="grey700">
                  Employee IDs: {employeeIds.join(', ')}
                </Text.Normal>
              )}
            </Box>
          ) : null
        }
        open={expanded}
      />
    </Box>
  )
}

export const ActivationResultSidePanel = ({ open, onClose, activationData }: Props) => {
  const { navigation } = useNavigation()

  const groupedIssues = React.useMemo(() => {
    if (!activationData) return []

    const employeeIssues: Record<number, string[]> = {}

    const { successfulEmployees: _successfulEmployees, ...issueFields } = activationData

    Object.entries(issueFields).forEach(([fieldName, employeeIds]) => {
      if (Array.isArray(employeeIds) && employeeIds.length > 0) {
        employeeIds.forEach((employeeId) => {
          if (!employeeIssues[employeeId]) {
            employeeIssues[employeeId] = []
          }
          employeeIssues[employeeId].push(fieldName)
        })
      }
    })

    const issueGroups: Record<string, { employeeIds: number[] }> = {}

    Object.entries(employeeIssues).forEach(([employeeId, issues]) => {
      const translatedIssues = issues.map(getErrorTranslation).sort()
      const issueKey = translatedIssues.join(', ')
      if (!issueGroups[issueKey]) {
        issueGroups[issueKey] = {
          employeeIds: [],
        }
      }
      issueGroups[issueKey].employeeIds.push(Number(employeeId))
    })

    return Object.entries(issueGroups).map(([issues, { employeeIds }]) => ({
      title: issues,
      count: employeeIds.length,
      employeeIds,
    }))
  }, [activationData])

  return (
    <Sidepanel
      open={open}
      onClose={onClose}
      title={i18n.t('benefits.tabs.employees.activation_results.title')}
      closeLabel={i18n.t('common.close')}
      width="wide"
    >
      <Box gap="s32">
        {activationData && (
          <Box flexDirection="column" gap="s24">
            <CalloutResults compact={false} activationData={activationData} />

            <Box flexDirection="column" gap="s16">
              <ResultSection
                title={i18n.t('benefits.tabs.employees.activation_results.successfully_activated')}
                count={activationData.successfulEmployees?.length || 0}
                employeeIds={activationData.successfulEmployees}
                employeeDetailsMap={activationData.employeeDetailsMap}
                navigation={navigation}
                showReviewLink={false}
              />

              {groupedIssues.map((issue, index) => (
                <ResultSection
                  key={index}
                  title={issue.title}
                  count={issue.count}
                  employeeIds={issue.employeeIds}
                  employeeDetailsMap={activationData.employeeDetailsMap}
                  navigation={navigation}
                />
              ))}
            </Box>
          </Box>
        )}

        {!activationData && (
          <Text.Normal size="300" color="grey800">
            {i18n.t('benefits.tabs.employees.activation_results.fields.general_error')}
          </Text.Normal>
        )}
      </Box>
    </Sidepanel>
  )
}
